<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcDashboardChart',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    url: {
      type: String,
      required: false,
    },
    dashboardId: {
      type: null,
      required: false,
    },
    title: {
      type: String,
      required: false,
    },
    height: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const refreshedUrl = ref(null);
  const ux = ref({
    loadingPlaceholder: 'none',
  });
  const b_2248226175642056_style = computed(() => ({
    height: props.height || '600px',
    overflow: 'hidden',
  }));
  const b_3343864469950044_style = computed(() => ({
    minHeight: 0,
  }));

  const $data = computed(() => ({
    refreshedUrl: refreshedUrl.value,
    ux: ux.value,
    b_2248226175642056_style: b_2248226175642056_style.value,
    b_3343864469950044_style: b_3343864469950044_style.value,
  }));

  const $$load_refreshedUrl = async () => {
    return null;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'refreshedUrl' },
      { name: 'ux' },
      { name: 'b_2248226175642056_style', nodeId: '2248226175642056' },
      { name: 'b_3343864469950044_style', nodeId: '3343864469950044' },
    ],
    loaders: { refreshedUrl: $$load_refreshedUrl },
    ux: { loadingPlaceholder: 'none' },
    dataRefs: {
      refreshedUrl,
      ux,
      b_2248226175642056_style,
      b_3343864469950044_style,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const displayUrl = computed(() => {
    if (loaded.value) {
      return refreshedUrl.value || props.url;
    }
    return null;
  });

  watch(
    () => props.url,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('url');
    }
  );
  watch(
    () => props.dashboardId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('dashboardId');
    }
  );
  watch(
    () => props.title,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('title');
    }
  );
  watch(
    () => props.height,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('height');
    }
  );

  const act_6705964765214521_setData_ltdr3v33f_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'refreshedUrl',
      value: $event.data.response.data.url,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_6705964765214521_request_tgyvnl6kp_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'get_superset_dashboard_token',
      parameters: {
        id: props.dashboardId,
      },
    };
  };

  const act_7315092382398562_reloadComponentData_74buck686 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_offs2p1jp =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_74buck686,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_offs2p1jp],
    event: 'reload',
  });
  const act_6705964765214521_setData_ltdr3v33f = new actionTypes.SetDataAction({
    actionArgs: act_6705964765214521_setData_ltdr3v33f_getActionArgs,
  });
  const inst_evh_6705964765214521_success_8wo8qoydn =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_setData_ltdr3v33f,
      event: 'success',
      displayName: 'setData',
    });
  const act_6705964765214521_request_tgyvnl6kp = new actionTypes.RequestAction({
    actionArgs: act_6705964765214521_request_tgyvnl6kp_getActionArgs,
    displayName: 'reload',
    events: [inst_evh_6705964765214521_success_8wo8qoydn],
  });
  const inst_evh_6705964765214521_click_x6ahf5c9m =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_request_tgyvnl6kp,
      event: 'click',
      displayName: 'reload',
    });
  const inst_evh_6705964765214521_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6705964765214521_click_x6ahf5c9m],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_reload_offs2p1jp,
    inst_evh_7315092382398562_reload,
    inst_evh_6705964765214521_success_8wo8qoydn,
    inst_evh_6705964765214521_click_x6ahf5c9m,
    reload: inst_evh_6705964765214521_click_x6ahf5c9m,
    inst_evh_6705964765214521_click,
  };

  const evh_7315092382398562_reload_offs2p1jp = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_offs2p1jp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_success_8wo8qoydn = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_success_8wo8qoydn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_click_x6ahf5c9m = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_click_x6ahf5c9m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_click = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    loadingPlaceholder="none"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="card" :style="b_2248226175642056_style">
      <div class="card-header d-flex align-items-center">
        <span class="fw-semibold">
          {{ title || gettext('Dashboard') }}
        </span>
        <div class="ms-auto"></div>
        <ItpButton
          icon="fa6-solid:arrows-rotate"
          :tooltip="gettext('Reload')"
          :spinning="actions.reload.isRunning"
          size="sm"
          variant="link"
          @click="evh_6705964765214521_click($event, {})"
        >
        </ItpButton>
      </div>
      <div
        class="card-body p-0 flex-grow-1"
        v-if="displayUrl"
        :style="b_3343864469950044_style"
      >
        <ItpKeepAliveIframe
          kind="url"
          placement="inline"
          :cacheKey="'dashboard-' + dashboardId"
          :src="displayUrl"
          class="w-100 h-100"
        >
        </ItpKeepAliveIframe>
      </div>
      <div
        class="card-body text-muted flex-grow-1"
        v-if="loaded && !displayUrl"
      >
        {{ gettext('This dashboard could not be loaded.') }}
      </div>
    </div>
  </ItpUsercontrol>
</template>
